/**
 * CoverLetterGeneratorInline — inline cover letter generation for a pipeline job.
 * Mirrors CVGeneratorInline pattern: quota check → optional company research → generate → preview.
 */

import { useState, useCallback, useMemo } from 'react';
import { Button, Alert, useToast } from '@/components/common';
import { CoverLetterPreview } from './CoverLetterPreview';
import { coverLetterService } from '@/services/coverLetter.service';
import { useDailyQuotaStore } from '@/store/daily-quota.store';
import { usePipelineStore } from '@/store/pipeline.store';
import { useCoverLetterStore, type StoredCoverLetter } from '@/store/cover-letter.store';
import type {
  CoverLetterTemplate,
  CompanyResearchResult,
  GeneratedCoverLetter,
} from '@shared/types/coverLetter';

interface CoverLetterGeneratorInlineProps {
  jobId: string;
  jobTitle: string;
  company: string;
  description?: string;
  jobUrl?: string;
}

type PipelinePhase = 'idle' | 'researching' | 'generating';

const TEMPLATES: { id: CoverLetterTemplate; label: string }[] = [
  { id: 'professional', label: 'Professional' },
  { id: 'modern', label: 'Modern' },
  { id: 'minimal', label: 'Minimal' },
];

export function CoverLetterGeneratorInline({
  jobId,
  jobTitle,
  company,
  description,
  jobUrl,
}: CoverLetterGeneratorInlineProps) {
  const toast = useToast();
  const attachCoverLetter = usePipelineStore((s) => s.attachCoverLetter);
  const addCoverLetter = useCoverLetterStore((s) => s.addCoverLetter);
  const coverLetters = useCoverLetterStore((s) => s.coverLetters);
  const existingLetters = useMemo(
    () => coverLetters.filter((cl) => cl.jobId === jobId),
    [coverLetters, jobId]
  );
  const canSave = useDailyQuotaStore((s) => s.canSaveCoverLetter);
  const consumeCoverLetter = useDailyQuotaStore((s) => s.consumeCoverLetter);
  const getRemainingCoverLetters = useDailyQuotaStore((s) => s.getRemainingCoverLetters);

  const [selectedTemplate, setSelectedTemplate] = useState<CoverLetterTemplate>('professional');
  const [companyUrl, setCompanyUrl] = useState('');
  const [includeResearch, setIncludeResearch] = useState(true);
  const [phase, setPhase] = useState<PipelinePhase>('idle');
  const [generatedLetter, setGeneratedLetter] = useState<GeneratedCoverLetter | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Show previously generated letters
  const [showPrevious, setShowPrevious] = useState(false);

  const quotaAvailable = canSave();
  const remaining = getRemainingCoverLetters();

  const normalizeUrl = (url: string): string => {
    let normalized = url.trim();
    if (!normalized) return '';
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = 'https://' + normalized;
    }
    return normalized;
  };

  const handleGenerate = useCallback(async () => {
    if (!quotaAvailable) return;

    setError(null);
    let research: CompanyResearchResult | null = null;

    // Step 1: Company research (optional)
    const normalizedUrl = normalizeUrl(companyUrl);
    if (includeResearch && normalizedUrl) {
      setPhase('researching');
      try {
        research = await coverLetterService.researchCompany(normalizedUrl);
      } catch (err) {
        // Research failure is non-fatal — proceed without it
        console.warn('Company research failed, continuing without:', err);
      }
    }

    // Step 2: Generate cover letter
    setPhase('generating');
    try {
      const letter = await coverLetterService.generateCoverLetter({
        vacancyData: {
          title: jobTitle,
          company,
          description: description || '',
          url: jobUrl,
        },
        companyResearch: research,
        options: { template: selectedTemplate },
      });

      // Consume quota
      consumeCoverLetter();

      // Store locally
      const stored: StoredCoverLetter = {
        ...letter,
        jobId,
        jobTitle,
        company,
      };
      addCoverLetter(stored);

      // Link to pipeline job
      attachCoverLetter(jobId, letter.id);

      setGeneratedLetter(letter);
      toast.success('Cover letter generated!');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Cover letter generation failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setPhase('idle');
    }
  }, [
    quotaAvailable,
    companyUrl,
    includeResearch,
    selectedTemplate,
    jobTitle,
    company,
    description,
    jobUrl,
    jobId,
    consumeCoverLetter,
    addCoverLetter,
    attachCoverLetter,
    toast,
  ]);

  const handleStartOver = useCallback(() => {
    setGeneratedLetter(null);
    setError(null);
  }, []);

  const isWorking = phase !== 'idle';

  return (
    <div className="cl-generator-inline">
      <h3 className="cl-generator-inline__title">Cover Letter</h3>
      <p className="cl-generator-inline__subtitle">
        Generate an AI-tailored cover letter for this job
      </p>

      {/* Quota indicator */}
      <div
        className={`cl-generator-inline__quota ${
          quotaAvailable
            ? 'cl-generator-inline__quota--available'
            : 'cl-generator-inline__quota--exhausted'
        }`}
      >
        {quotaAvailable ? (
          <>
            <span className="cl-generator-inline__quota-count">{remaining}</span>
            <span>cover letters remaining today</span>
          </>
        ) : (
          <span>Daily limit reached. Try again tomorrow.</span>
        )}
      </div>

      {/* Previously generated letters */}
      {existingLetters.length > 0 && !generatedLetter && !isWorking && (
        <div className="cl-generator-inline__previous">
          <button
            className="cl-generator-inline__previous-toggle"
            onClick={() => setShowPrevious(!showPrevious)}
          >
            {showPrevious ? 'Hide' : 'Show'} {existingLetters.length} previous cover letter
            {existingLetters.length !== 1 ? 's' : ''}
          </button>
          {showPrevious &&
            existingLetters.map((letter) => (
              <div key={letter.id} className="cl-generator-inline__previous-item">
                <CoverLetterPreview
                  content={letter.content}
                  letterText={letter.letterText}
                  template={letter.template}
                  wordCount={letter.wordCount}
                  atsKeywordCoverage={letter.atsKeywordCoverage}
                />
              </div>
            ))}
        </div>
      )}

      {/* Generation UI (pre-generation state) */}
      {!generatedLetter && !isWorking && (
        <>
          {/* Company URL input (optional) */}
          <div className="cl-generator-inline__field">
            <label className="cl-generator-inline__label" htmlFor="company-url">
              Company website
              <span className="cl-generator-inline__label-hint">(optional — improves quality)</span>
            </label>
            <input
              id="company-url"
              type="text"
              className="cl-generator-inline__input"
              placeholder="e.g. company.com"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
            />
            {companyUrl && (
              <label className="cl-generator-inline__checkbox-label">
                <input
                  type="checkbox"
                  checked={includeResearch}
                  onChange={(e) => setIncludeResearch(e.target.checked)}
                />
                <span>Research company before generating</span>
              </label>
            )}
          </div>

          {/* Template selector */}
          <div className="cl-generator-inline__template-section">
            <p className="cl-generator-inline__template-label">Template</p>
            <div className="cl-generator-inline__template-grid">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  className={`cl-generator-inline__template-btn ${
                    selectedTemplate === t.id
                      ? 'cl-generator-inline__template-btn--selected'
                      : ''
                  }`}
                  onClick={() => setSelectedTemplate(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <div className="cl-generator-inline__actions">
            <Button onClick={handleGenerate} disabled={!quotaAvailable} fullWidth>
              {companyUrl && includeResearch
                ? 'Research & Generate'
                : 'Generate Cover Letter'}
            </Button>
          </div>

          {error && (
            <div className="cl-generator-inline__error">
              <Alert variant="error">{error}</Alert>
            </div>
          )}
        </>
      )}

      {/* Loading state */}
      {isWorking && (
        <div className="cl-generator-inline__loading">
          <div className="cl-generator-inline__spinner" />
          <p className="cl-generator-inline__loading-text">
            {phase === 'researching' ? 'Researching company...' : 'Generating cover letter...'}
          </p>
          <p className="cl-generator-inline__loading-subtext">
            {phase === 'researching'
              ? 'Analyzing company website for culture, values, and tone.'
              : 'AI is crafting a tailored cover letter. This may take up to 2 minutes.'}
          </p>
        </div>
      )}

      {/* Post-generation result */}
      {generatedLetter && !isWorking && (
        <div className="cl-generator-inline__result">
          <div className="cl-generator-inline__result-header">
            <p className="cl-generator-inline__result-title">Cover Letter Generated</p>
            <Button variant="ghost" size="sm" onClick={handleStartOver}>
              Generate New
            </Button>
          </div>
          <CoverLetterPreview
            content={generatedLetter.content}
            letterText={generatedLetter.letterText}
            template={generatedLetter.template}
            wordCount={generatedLetter.wordCount}
            atsKeywordCoverage={generatedLetter.atsKeywordCoverage}
          />
        </div>
      )}
    </div>
  );
}
